import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Pagination from '../../../common/Pagination';
import { getNavIconClass } from '../../../../navigation/clinicNavConfig';
import { getReputationAvatarTheme } from '../subparts/reputationTheme';
import {
    formatReputationDate,
    getCommentStatusMeta,
    getPatientInitials,
} from '../subparts/reputationQuery';

function CommentsTab({ patientComments, onPageChange }) {
    const [currentPage, setCurrentPage] = useState(0);
    const comments = patientComments?.content || [];
    const totalElements = patientComments?.totalElements ?? 0;

    const handlePageChange = (page) => {
        setCurrentPage(page);
        onPageChange(page);
    };

    return (
        <div className="h-100 d-flex flex-column min-h-0">
            <div className="reputation-toolbar">
                <div>
                    <h2 className="reputation-toolbar__title">Patient Comments</h2>
                    <p className="reputation-toolbar__subtitle mb-0">
                        Comments submitted through your reputation page
                    </p>
                </div>
                <span className="reputation-toolbar__summary">
                    {totalElements} comment{totalElements === 1 ? '' : 's'}
                </span>
            </div>

            <div className="reputation-tab-content">
                <div className="reputation-comments-note" role="note">
                    <i className="bi bi-info-circle" aria-hidden="true"></i>
                    <p className="mb-0">
                        The name shown with each comment is what the patient entered and may differ from
                        the name on file. Open the patient profile to see their official record.
                    </p>
                </div>

                {comments.length > 0 ? (
                    <>
                        <div className="reputation-list">
                            {comments.map((comment, index) => {
                                const statusMeta = getCommentStatusMeta(comment.status);
                                const patientName =
                                    [comment.firstName, comment.lastName].filter(Boolean).join(' ').trim() ||
                                    'Patient';

                                return (
                                    <article key={comment.id} className="reputation-comment-card">
                                        <div className="reputation-comment-card__header">
                                            <div className="reputation-comment-card__identity">
                                                <span
                                                    className={`reputation-avatar ecx-nav-icon ${getNavIconClass(
                                                        getReputationAvatarTheme(comment.patientId, index)
                                                    )}`}
                                                >
                                                    {getPatientInitials(comment.firstName, comment.lastName)}
                                                </span>
                                                <div className="min-width-0">
                                                    <h3 className="reputation-comment-card__name">
                                                        {comment.patientId ? (
                                                            <Link
                                                                to={`/patient-files/${comment.patientId}`}
                                                                className="reputation-patient-link"
                                                            >
                                                                {patientName}
                                                            </Link>
                                                        ) : (
                                                            patientName
                                                        )}
                                                    </h3>
                                                    <div className="reputation-comment-card__patient-id">
                                                        Patient ID: {comment.patientId || '—'}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className={statusMeta.className}>{statusMeta.label}</span>
                                        </div>
                                        <div className="reputation-comment-card__body">
                                            <p className="reputation-comment-card__notes">
                                                {comment.notes || '—'}
                                            </p>
                                            <div className="reputation-comment-card__footer">
                                                {formatReputationDate(comment.createdAt)}
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {patientComments.totalPages > 1 && (
                            <div className="reputation-pagination">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={patientComments.totalPages}
                                    onPageChange={handlePageChange}
                                    totalElements={totalElements}
                                    pageSize={patientComments.pageable?.pageSize || 10}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="reputation-empty">
                        <div className="reputation-empty__icon" aria-hidden="true">
                            <i className="bi bi-chat-dots"></i>
                        </div>
                        <h3 className="h6 fw-normal text-dark mb-1">No patient comments</h3>
                        <p className="mb-0 small text-muted">
                            Patient comments will appear here once they are submitted.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CommentsTab;
